import { Injectable, NgZone } from '@angular/core';
import { initializeApp, getApps, getApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, Messaging } from 'firebase/messaging';
import { environment } from '../../environments/environment';
import { HelperService } from '../api-common-service/helper.service';

@Injectable({
  providedIn: 'root'
})
export class FcmService {
  private messaging: Messaging | null = null;

  constructor(public helper: HelperService, private zone: NgZone) {
    this.initFirebase();
  }

  private initFirebase() {
    try {
      if (typeof window !== 'undefined' && environment.firebase?.apiKey) {
        const app = !getApps().length ? initializeApp(environment.firebase) : getApp();
        this.messaging = getMessaging(app);
        console.log('[FCM] Firebase Messaging initialized successfully.');
      }
    } catch (err) {
      console.error('[FCM] Failed to initialize Firebase Messaging:', err);
    }
  }

  /**
   * Request notification permission and get FCM token (Universal & Bulletproof)
   */
  async requestPermission(): Promise<string | null> {
    console.log('[FCM] Starting requestPermission workflow...');
    try {
      if (typeof window === 'undefined' || !('Notification' in window)) {
        console.warn('[FCM] Notifications are not supported in this browser environment.');
        return localStorage.getItem('fcm_token');
      }

      const currentPermission = Notification.permission;
      console.log('[FCM] Current Notification.permission:', currentPermission);

      let permission = currentPermission;
      if (permission !== 'granted') {
        permission = await Notification.requestPermission();
        console.log('[FCM] User answered permission prompt with:', permission);
      }

      if (permission === 'granted') {
        if (!this.messaging) {
          this.initFirebase();
        }
        if (this.messaging) {
          let swRegistration: ServiceWorkerRegistration | undefined;
          if ('serviceWorker' in navigator) {
            try {
              const baseHref = document.querySelector('base')?.getAttribute('href') || '/';
              const normalizedBase = baseHref.startsWith('/') ? baseHref : '/' + baseHref;
              const swUrl = `${window.location.origin}${normalizedBase}firebase-messaging-sw.js`;
              swRegistration = await navigator.serviceWorker.register(swUrl, { scope: normalizedBase });
              if (swRegistration) {
                await swRegistration.update();
              }
              await navigator.serviceWorker.ready;
              console.log('[FCM] ServiceWorker registered and ready at:', swUrl);
            } catch (swErr) {
              console.warn('[FCM] ServiceWorker registration warning:', swErr);
            }
          }

          const vapidKey = environment.firebase?.vapidKey || 'BE0CblSZ7W60TIyyVsUAF2vZhDQMHys9vxb5wF14iLmpKVvX1OWfD-ZhVVWb2RCmcKpjqcqP9X-7fnGEdJTpVIE';
          console.log('[FCM] Requesting token with VAPID key:', vapidKey);

          const options: { vapidKey?: string; serviceWorkerRegistration?: ServiceWorkerRegistration } = {
            vapidKey: vapidKey
          };
          if (swRegistration) {
            options.serviceWorkerRegistration = swRegistration;
          }

          const token = await getToken(this.messaging, options);
          if (token) {
            console.log('✅ [FCM] FCM Token obtained:', token);
            localStorage.setItem('fcm_token', token);
            return token;
          } else {
            console.warn('[FCM] getToken returned an empty string or null.');
          }
        }
      } else {
        console.warn('[FCM] Notification permission was denied or dismissed:', permission);
      }

      const fallbackToken = localStorage.getItem('fcm_token');
      if (fallbackToken) {
        console.log('[FCM] Using cached FCM token from localStorage:', fallbackToken);
        return fallbackToken;
      }
      return null;
    } catch (err) {
      console.error('❌ [FCM] Error in requestPermission:', err);
      const fallbackToken = localStorage.getItem('fcm_token');
      if (fallbackToken) {
        console.log('[FCM] Error occurred, returning cached token:', fallbackToken);
        return fallbackToken;
      }
      return null;
    }
  }

  /**
   * Listen for foreground messages
   */
  listenForMessages() {
    if (!this.messaging) return;
    
    onMessage(this.messaging, (payload) => {
      this.zone.run(() => {
        console.log('[FCM] Foreground FCM Message received via SDK:', payload);
        const payloadAny = payload as any;
        const title = payload.notification?.title || payload.data?.['title'] || payloadAny.title || 'Notification';
        const body = payload.notification?.body || payload.data?.['body'] || payloadAny.body || '';
        
        this.helper.showNotificationPopup(title, body);
        this.helper.notificationSub.next(true);
      });
    });

    // Listen to messages broadcasted by our custom service worker push interceptor via BroadcastChannel
    if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
      const channel = new BroadcastChannel('fcm_notification_channel');
      channel.onmessage = (event) => {
        this.zone.run(() => {
          if (event.data) {
            if (event.data.type === 'FCM_FOREGROUND_REFRESH') {
              console.log('[FCM] Foreground FCM Message received via BroadcastChannel:', event.data.payload);
              
              const payload = event.data.payload;
              const payloadAny = payload as any;
              
              // If the SW determined the user is actively viewing this tab, show a proper in-app notification card
              if (event.data.showInApp) {
                const title = payload.notification?.title || payload.data?.['title'] || payloadAny.title || 'Notification';
                const body = payload.notification?.body || payload.data?.['body'] || payloadAny.body || '';
                
                this.helper.showNotificationPopup(title, body, payload);
              }
              
              this.helper.notificationSub.next(true);
            } else if (event.data.type === 'FCM_CLICK_NAVIGATE') {
              console.log('[FCM] FCM Notification click navigation received via BroadcastChannel:', event.data.payload);
              const payload = event.data.payload;
              const url = this.helper.getNotificationRedirectUrl(payload);
              if (url) {
                this.helper.navigateByUrl(url);
              }
            }
          }
        });
      };
    }

    // Direct Service Worker message listener fallback (in case BroadcastChannel is blocked by browser sandbox/policies)
    if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        this.zone.run(() => {
          if (event.data) {
            if (event.data.type === 'FCM_FOREGROUND_REFRESH') {
              console.log('[FCM] Foreground FCM Message received via ServiceWorker direct channel:', event.data.payload);
              
              const payload = event.data.payload;
              const payloadAny = payload as any;
              
              if (event.data.showInApp) {
                const title = payload.notification?.title || payload.data?.['title'] || payloadAny.title || 'Notification';
                const body = payload.notification?.body || payload.data?.['body'] || payloadAny.body || '';
                
                this.helper.showNotificationPopup(title, body, payload);
              }
              
              this.helper.notificationSub.next(true);
            } else if (event.data.type === 'FCM_CLICK_NAVIGATE') {
              console.log('[FCM] FCM Notification click navigation received via ServiceWorker direct channel:', event.data.payload);
              const payload = event.data.payload;
              const url = this.helper.getNotificationRedirectUrl(payload);
              if (url) {
                this.helper.navigateByUrl(url);
              }
            }
          }
        });
      });
    }
  }
}
